import axios from 'axios';
import { axiosInstance } from './api';
import { API_BASE } from './base';

// «Отзывы» (plugins/reviews, Alexey 16.09).

export type MapProvider = 'yandex' | '2gis';
export type ReviewProvider = MapProvider | 'linza';
export type TextSize = 'sm' | 'md' | 'lg' | 'xl';
export type FormIdentity = 'linza' | 'surf';
export type FormTextKey = 'title' | 'address' | 'prompt' | 'prompt_subtitle' | 'hint' | 'rating_label';
export type TextWeight = 'normal' | 'bold';
/** caps — автокапс (у Surf заголовок рисуется капслоком); false выключает его. */
export type FormTexts = Partial<Record<FormTextKey, { text: string; size: TextSize; weight?: TextWeight; caps?: boolean }>>;

export interface ReviewSourceDto {
  provider: MapProvider;
  url: string;
  external_id: string;
  public_url: string;
  rating: number | null;
  rating_count: number | null;
  review_count: number | null;
  last_attempt_at: string | null;
  last_success_at: string | null;
  last_error: string;
  review_count_delta?: number | null;
  rating_at_start?: number | null;
  /** Сколько отзывов этого источника реально пришло за период. */
  new_in_period?: number;
  /** Сколько отзывов этого источника лежит у нас — может быть меньше review_count. */
  stored_count?: number;
  /** Лог ПОКАЗАННОЙ оценки источника по дням. Копится с 16.09.2026 — раньше
   *  этой даты точек нет ни у одного источника. */
  rating_series?: { day: string; rating: number }[];
}

/** Настройки формы: звёзды можно выключить (п. 9), блок «поделиться контактом»
 *  включается с редактируемым текстом над ним (п. 27). */
export interface FormOptions {
  stars: boolean;
  /** Одно необязательное поле контакта; text — его подпись (AI16.7). */
  contact: { enabled: boolean; text: string };
  /** Логотип в центре QR — свой выбор, не связан с оформлением формы (AI16.4). */
  qr_logo?: QrLogo;
  /** Карточка для печати (Алексей 22.10, #1976). */
  qr_card?: QrCardOptions;
}
export type QrCardTemplate = 'surf_wave' | 'surf_paper' | 'surf_black' | 'poster' | 'journal' | 'glass';
/** template null — первый шаблон своего набора (Surf / остальные); bg_id: null — фон шаблона,
 *  0 — без фото, −N — встроенный, N — загруженный; title — филиал, legend — заголовок,
 *  tagline null — подпись шаблона по умолчанию. */
export interface QrCardOptions {
  template: QrCardTemplate | null;
  bg_id: number | null;
  title: string;
  legend: string;
  tagline: string | null;
  frame_opacity: number;
}
export type QrLogo = 'robot' | 'surf' | 'none' | 'custom';
/** text — единое поле; остальные ключи — отзывы, оставленные раньше. */
export interface ReviewContact { text?: string; email?: string; phone?: string; messenger?: string; note?: string }

export interface ReviewFormDto {
  public_id: string;
  is_active: boolean;
  identity: FormIdentity;
  /** Фирменный стиль сети филиала из её плагина (BE reviews _brand_style, #1279); null — сети/стиля нет. */
  brand_style?: { identity: FormIdentity; logo: string; caption_caps: boolean } | null;
  show_address: boolean;
  texts: FormTexts;
  options: FormOptions;
  has_logo: boolean;
  logo_url: string | null;
  updated_at: string;
}

export interface ReviewsSettingsDto {
  address: string;
  branch_name: string;
  sources: Record<MapProvider, ReviewSourceDto | null>;
  form: ReviewFormDto | null;
  /** Сколько отзывов пришло с нашей формы; 0 — код ещё не распечатали (AI37). */
  qr_review_count?: number;
}

export type Severity = 'minor' | 'normal' | 'major' | 'critical';

export interface ReviewAnalysis {
  sentiment: 'positive' | 'mixed' | 'negative' | 'neutral';
  problems: { category: string; summary: string; severity: Severity; quote?: string }[];
  /** Строки — разбор первой версии; объекты — по общим темам (v2). */
  praise: (string | { category: string; summary: string; quote?: string })[];
  v?: number;
}

export interface ReviewDto {
  id: number;
  provider: ReviewProvider;
  rating: number | null;
  text: string;
  author_name: string;
  published_at: string;
  url: string;
  business_reply: string;
  analysis: ReviewAnalysis | null;
  /** Контакт гостя из QR-формы, если оставил. */
  contact?: ReviewContact | null;
}

/** Тема отзывов — общая и для похвалы, и для жалоб. У похвалы нет ни severity,
 *  ни pain, ни rating_impact (он null): при счёте от потолка 5.0 хвалебные
 *  отзывы и так пятёрки, добавлять им нечего. У жалоб rating_impact —
 *  ОТРИЦАТЕЛЬНЫЙ и уже готовый к показу вклад в среднюю: сумма по темам плюс
 *  нераспределённое и потолок дают ровно среднюю (Алексей 18.09, AI52). */
export interface ReviewThemeStat {
  category: string;
  count: number;
  share: number;
  avg_rating_with: number | null;
  avg_rating_without: number | null;
  rating_impact: number | null;
  severity?: Severity;
  pain?: number;
  /** Объективные формулировки из разборов. */
  examples: string[];
  /** Дословные фрагменты отзывов — для подсказки и жирного выделения в ленте. */
  quotes: { review_id: number; text: string }[];
  review_ids: number[];
}
export type ReviewProblemStat = ReviewThemeStat;

export interface ReviewsStatsDto {
  from: string;
  to: string;
  sources: ReviewSourceDto[];
  form: ReviewFormDto | null;
  period: {
    count: number;
    avg_rating: number | null;
    by_provider: Partial<Record<ReviewProvider, number>>;
    by_rating: Record<string, number>;
    analyzed: number;
    pending_analysis: number;
    /** Динамика по дням периода — считается на сервере: список в ленте больше не полон. */
    by_day: { day: string; count: number; avg_rating: number | null; avg_to_date: number | null }[];
    /** Сколько отзывов за период остались без ответа заведения (QR-форма не считается). */
    unanswered: number;
    unanswered_ids?: number[];
    /** Средняя за всё время до периода и после него — сколько период добавил к средней. */
    avg_before?: number | null;
    avg_after?: number | null;
    /** Свод, который обязан сходиться: потолок + вычеты тем + нераспределённое = средняя. */
    rating_ceiling?: number;
    themes_drop?: number;
    drop_unattributed?: number;
  };
  /** Из чего складывается средняя: вклад каждой темы и её потенциал роста. */
  rating_breakdown?: RatingBreakdown;
  problems: ReviewThemeStat[];
  /** Что хвалят — по тем же темам, что и жалобы. */
  praise: ReviewThemeStat[];
  /** Оценка ОТВЕТОВ заведения по осям — из сохранённого разбора, едет вместе со
   *  статистикой (не отдельным поздним запросом). null — за период нет отзывов. */
  reply?: ReplyAgg | null;
  reviews: ReviewDto[];
}

/** Одна ось оценки ответов: ключи уровней worst→best, распределение и средняя
 *  позиция (дробный индекс) для маркера на шкале. */
export interface ReplyAxisAgg {
  order: string[];
  dist: Record<string, number>;
  n: number;
  avg: number | null;
}

export interface ReplyAgg {
  total: number;
  answered: number;
  unanswered: number;
  coverage: number;
  analyzed: number;
  axes: { tone: ReplyAxisAgg; relevance: ReplyAxisAgg; liveliness: ReplyAxisAgg };
}

/** Раскладка средней оценки (Алексей 18.09, AI54).
 *  Два точных счёта: сумма contribution по темам и «без темы» = средняя;
 *  сумма potential + «без названной причины» = 5 − средняя. */
export interface RatingBreakdownTheme {
  category: string;
  /** В скольких отзывах тема названа. */
  count: number;
  /** Какая доля средней приходится на тему (веса в сумме дают 1). */
  weight: number;
  /** Вклад темы в среднюю: складывается со всеми в саму среднюю. */
  contribution: number;
  /** На сколько вырастет средняя в пределе, если тему закрыть. */
  potential: number;
  /** Собственная средняя темы. */
  avg_own: number | null;
  /** Тянет среднюю вверх (+) или вниз (−). */
  pull: number;
}

export interface RatingBreakdown {
  themes: RatingBreakdownTheme[];
  no_theme: { count: number; weight: number; contribution: number; potential: number } | null;
  avg: number | null;
  ceiling: number;
  total_contribution: number;
  total_potential: number;
}

export interface PublicFormDto {
  branch_name: string;
  address: string;
  identity: FormIdentity;
  texts: FormTexts;
  options?: FormOptions;
  logo_url: string | null;
}

export const getReviewsSettings = (zoneId: string) =>
  axiosInstance.get<ReviewsSettingsDto>(`/reviews/zones/${zoneId}/settings/`);

export const putReviewSource = (zoneId: string, provider: MapProvider, url: string) =>
  axiosInstance.put<{ source: ReviewSourceDto | null }>(`/reviews/zones/${zoneId}/sources/${provider}/`, { url });

export const putReviewForm = (
  zoneId: string,
  payload: Partial<Pick<ReviewFormDto, 'identity' | 'show_address' | 'texts' | 'is_active'>> & { address?: string; options?: Partial<FormOptions> | { contact: Partial<FormOptions['contact']> } | { qr_logo: QrLogo } | { qr_card: Partial<QrCardOptions> } },
) => axiosInstance.put<{ form: ReviewFormDto; address: string }>(`/reviews/zones/${zoneId}/form/`, payload);

export const uploadReviewFormLogo = (zoneId: string, file: File) => {
  const body = new FormData();
  body.append('file', file);
  return axiosInstance.post<{ form: ReviewFormDto }>(`/reviews/zones/${zoneId}/form/logo/`, body);
};

export interface QrBackgroundDto { id: number; source: 'upload' | 'generated'; prompt: string; created_at: string; url: string }
export interface QrBackgroundsDto { backgrounds: QrBackgroundDto[]; generations_left: number; created?: QrBackgroundDto }

export const getQrBackgrounds = (zoneId: string) =>
  axiosInstance.get<QrBackgroundsDto>(`/reviews/zones/${zoneId}/qr-backgrounds/`);

export const uploadQrBackground = (zoneId: string, file: File) => {
  const body = new FormData();
  body.append('file', file);
  return axiosInstance.post<QrBackgroundsDto>(`/reviews/zones/${zoneId}/qr-backgrounds/`, body);
};

/** Генерация идёт до ~1,5 мин (Replicate). */
export const generateQrBackground = (zoneId: string, prompt: string) =>
  axiosInstance.post<QrBackgroundsDto>(`/reviews/zones/${zoneId}/qr-backgrounds/generate/`, { prompt }, { timeout: 180_000 });

export const deleteQrBackground = (zoneId: string, id: number) =>
  axiosInstance.delete<QrBackgroundsDto>(`/reviews/zones/${zoneId}/qr-backgrounds/${id}/file/`);

/** Файл фона под JWT — в <img> его не отдать, берём blob. */
export const fetchQrBackgroundBlob = (path: string) =>
  axiosInstance.get<Blob>(path, { responseType: 'blob' }).then((r) => r.data);

export const deleteReviewFormLogo = (zoneId: string) =>
  axiosInstance.delete<{ form: ReviewFormDto }>(`/reviews/zones/${zoneId}/form/logo/`);

/** Ступени шкалы «как отвечают на отзывы», от худшей к лучшей (AI54.2). */
export type ReplyStyle = 'rude' | 'none' | 'dull' | 'generated' | 'manual' | 'personal';

export interface ReplyStyleDto {
  /** Отзывов за период и сколько из них с ответом — это арифметика, не модель. */
  total: number;
  answered: number;
  coverage: number;
  /** null — модель не ответила; блок тогда не рисуем. */
  style: ReplyStyle | null;
  label: string | null;
  rank: number | null;
  note: string;
  /** Вся шкала с подписями — чтобы фронт не держал второй справочник. */
  styles: Record<ReplyStyle, string>;
}

export const getReplyStyle = (zoneId: string, from: string, to: string) =>
  axiosInstance.get<ReplyStyleDto | Record<string, never>>(`/reviews/zones/${zoneId}/reply-style/`, {
    params: { from, to },
  });

export const getReviewsStats = (zoneId: string, from: string, to: string) =>
  axiosInstance.get<ReviewsStatsDto>(`/reviews/zones/${zoneId}/stats/`, { params: { from, to } });

export interface ReviewsPageDto {
  reviews: ReviewDto[];
  /** Курсор следующей порции; null — дальше ничего нет. */
  next_before: string | null;
}

/** Лента отзывов по курсору: то, что подгружается прокруткой ниже периода. */
export const getReviewsPage = (zoneId: string, before: string | null, limit = 30) =>
  axiosInstance.get<ReviewsPageDto>(`/reviews/zones/${zoneId}/list/`, {
    params: { ...(before ? { before } : {}), limit },
  });

// Public, unauthenticated — plain axios so no JWT header / refresh interceptor
// is involved for a guest scanning the QR code.
const publicHttp = axios.create({ baseURL: API_BASE, timeout: 20000 });

export const getPublicForm = (publicId: string) =>
  publicHttp.get<PublicFormDto>(`/reviews/public/forms/${publicId}/`);

export const submitPublicReview = (publicId: string, payload: { rating: number | null; text: string; website: string; contact?: ReviewContact }) =>
  publicHttp.post<{ ok: boolean }>(`/reviews/public/forms/${publicId}/submit/`, payload);

/** Absolute URL for an API-relative path (logo endpoint). */
export const apiUrl = (path: string) => `${API_BASE}${path}`;

/** The link printed into the QR code. */
/** Ссылка на публичную форму. Берём текущий origin, а не прод-домен: на стейджинге
 *  захардкоженный my.linzametrics.com уводил проверяющего на боевую страницу
 *  другого стенда (Алексей 16.09). */
export const publicReviewUrl = (publicId: string) =>
  `${typeof window === 'undefined' ? 'https://my.linzametrics.com' : window.location.origin}/leave_review/${publicId}`;
